import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@app/data-layer/enums/documents';
import { DocumentModel } from '@app/data-layer/models/documents';
import { IsfModel } from '@app/data-layer/models/isf';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from '@services/utils/utils.service';

interface UploadDocumentModel {
  file: File;
  doc: DocumentModel;
}
interface DeleteDocumentModel {
  doc: DocumentModel;
}
interface StateUpdateDocumentModel {
  state: enumDocumentState;
  doc: DocumentModel;
}
interface UploadNewDocumentModel {
  file: File;
  type: enumDocumentType;
}

@Component({
  selector: 'app-isf-documents-box',
  templateUrl: './isf-documents-box.component.html',
  styleUrls: ['./isf-documents-box.component.scss'],
})
export class IsfDocumentsBoxComponent implements OnChanges {
  @Input() public isf: IsfModel;
  @Input() public isfDocuments: DocumentModel[] = [];
  @Input() public additionalDocumentsOptions: enumDocumentType[] = [];
  @Input() public isShowSkipOcrScanControl: boolean = true;
  @Input() public isShowHeaderTitle: boolean = true;
  @Input() public isShowDownloadAllDocs: boolean = false;
  @Input() public isShowDeleteDocument: boolean = true;
  @Input() public skipOcrScanControl: UntypedFormControl;
  @Input() public isLoadingDocUploadFlags: Map<string, boolean> = new Map();

  @Output() public upload: EventEmitter<UploadDocumentModel> = new EventEmitter<UploadDocumentModel>();
  @Output() public preview: EventEmitter<DocumentModel> = new EventEmitter<DocumentModel>();
  @Output() public delete: EventEmitter<DeleteDocumentModel> = new EventEmitter<DeleteDocumentModel>();
  @Output() public downloadAll: EventEmitter<void> = new EventEmitter<void>();
  @Output() public stateUpdate: EventEmitter<StateUpdateDocumentModel> = new EventEmitter<StateUpdateDocumentModel>();
  @Output() public uploadNewDocument: EventEmitter<UploadNewDocumentModel> = new EventEmitter<UploadNewDocumentModel>();

  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public localAdditionalDocuments: DocumentModel[] = [];
  public loadingLocalDocId: number | null = null;
  private additionalDocCounter: number = -100;
  private uploadedLocalDocIds: Set<number> = new Set();
  private initialTypeCountsForLocalDocs: Map<number, number> = new Map();
  private previousRealCountByType: Map<number, number> = new Map();

  constructor(private utilsService: UtilsService, private translateService: TranslateService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isfDocuments) {
      // 1. Reset loading state on the card that triggered the upload
      if (this.loadingLocalDocId != null) {
        const loadingId = this.loadingLocalDocId;
        this.loadingLocalDocId = null;
        this.localAdditionalDocuments = this.localAdditionalDocuments.map((d) => (d.id === loadingId ? { ...d } : d));
      }

      // 2. Detect deletes via REAL doc counts (docs with createdDate).
      if (this.localAdditionalDocuments.length > 0 && this.previousRealCountByType.size > 0) {
        const typesToCheck = new Set<number>();
        this.localAdditionalDocuments.forEach((d) => typesToCheck.add(d.type));

        const idsToRemove: number[] = [];
        typesToCheck.forEach((type) => {
          const prevRealCount = this.previousRealCountByType.get(type) || 0;
          const currRealCount = this.isfDocuments?.filter((d) => d.type === type && !!d.createdDate).length || 0;
          if (currRealCount < prevRealCount) {
            const decrease = prevRealCount - currRealCount;
            const localOfType = this.localAdditionalDocuments.filter((d) => d.type === type);
            const sortedCandidates = [
              ...localOfType.filter((d) => this.uploadedLocalDocIds.has(d.id)),
              ...localOfType.filter((d) => !this.uploadedLocalDocIds.has(d.id)),
            ];
            for (let i = 0; i < Math.min(decrease, sortedCandidates.length); i++) {
              idsToRemove.push(sortedCandidates[i].id);
            }
          }
        });

        if (idsToRemove.length > 0) {
          idsToRemove.forEach((id) => {
            this.uploadedLocalDocIds.delete(id);
            this.initialTypeCountsForLocalDocs.delete(id);
          });
          this.localAdditionalDocuments = this.localAdditionalDocuments.filter((d) => !idsToRemove.includes(d.id));
        }
      }

      // 3. Remove absorbed uploaded cards — cards whose uploads are now reflected in server docs.
      if (this.localAdditionalDocuments.length > 0) {
        const typeStats = new Map<number, { minInitial: number; uploadedCount: number }>();
        this.localAdditionalDocuments.forEach((d) => {
          if (this.uploadedLocalDocIds.has(d.id)) {
            const initial = this.initialTypeCountsForLocalDocs.get(d.id) || 0;
            const stats = typeStats.get(d.type);
            if (stats) {
              stats.uploadedCount++;
              stats.minInitial = Math.min(stats.minInitial, initial);
            } else {
              typeStats.set(d.type, { minInitial: initial, uploadedCount: 1 });
            }
          }
        });

        const absorbedCountByType = new Map<number, number>();
        const absorbedIds: number[] = [];
        this.localAdditionalDocuments.forEach((d) => {
          if (this.uploadedLocalDocIds.has(d.id)) {
            const stats = typeStats.get(d.type);
            if (stats) {
              const currentRealCount = this.isfDocuments?.filter((ed) => ed.type === d.type && !!ed.createdDate).length || 0;
              const surplus = Math.max(currentRealCount - stats.minInitial, 0);
              const maxAbsorb = Math.min(surplus, stats.uploadedCount);
              const alreadyAbsorbed = absorbedCountByType.get(d.type) || 0;
              if (alreadyAbsorbed < maxAbsorb) {
                absorbedIds.push(d.id);
                absorbedCountByType.set(d.type, alreadyAbsorbed + 1);
              }
            }
          }
        });

        if (absorbedIds.length > 0) {
          absorbedIds.forEach((id) => {
            this.uploadedLocalDocIds.delete(id);
            this.initialTypeCountsForLocalDocs.delete(id);
          });
          this.localAdditionalDocuments = this.localAdditionalDocuments.filter((d) => !absorbedIds.includes(d.id));
        }
      }

      // 4. Update previous REAL doc counts (only docs with createdDate)
      const newRealCounts = new Map<number, number>();
      this.isfDocuments?.forEach((d) => {
        if (d.createdDate) {
          newRealCounts.set(d.type, (newRealCounts.get(d.type) || 0) + 1);
        }
      });
      this.previousRealCountByType = newRealCounts;
    }
  }

  public document(doc: DocumentModel): DocumentModel {
    if (!this.isfDocuments) {
      return;
    }

    return this.isfDocuments?.find((document: DocumentModel) => {
      return document.id === doc.id && doc.relatedEntityId === this.isf?.id;
    });
  }

  public documentName(doc: DocumentModel): string {
    const currentShipmentOfTypeDocs = this.isfDocuments?.filter((cdotd) => cdotd.type === doc.type);
    const indexOfCurrentShipmentOfTypeDoc = currentShipmentOfTypeDocs.findIndex((cdotd) => cdotd.id === doc.id);
    const documentTypeName = enumDocumentType[doc.type];
    const blTypeDocTitlePath = `ENTRY_SUMMARY.BILL_OF_LADING_DOCS.${documentTypeName}.TITLE`;
    const documentTypeTitlePath =
      doc.type === enumDocumentType.ISF_FORM ? 'LABELS.ISF_INSTRUCTION_SHEET' : `DOCUMENT_TYPE.${documentTypeName}`;

    const isBlDocTranslatedTextExist = !!this.utilsService.getTextAtPath(blTypeDocTitlePath);
    const translatedTitle = isBlDocTranslatedTextExist
      ? this.translateService.instant(blTypeDocTitlePath)
      : this.translateService.instant(documentTypeTitlePath);

    const documentName = !!indexOfCurrentShipmentOfTypeDoc ? translatedTitle + ` #${indexOfCurrentShipmentOfTypeDoc + 1}` : translatedTitle;

    return documentName;
  }

  public documentHint(docType: enumDocumentType): string {
    const documentTypeName = enumDocumentType[docType];
    return `DOCUMENT_HINT.${documentTypeName}`;
  }

  public onUploadDocument(file: File, doc: DocumentModel): void {
    this.upload.emit({ file, doc });
  }

  public onDeleteDocument(doc: DocumentModel): void {
    this.delete.emit({ doc });
  }

  public onPreviewDocument(doc: DocumentModel) {
    this.preview.emit(doc);
  }

  public onUpdateDocumentState(state: enumDocumentState, doc: DocumentModel): void {
    this.stateUpdate.emit({ state, doc });
  }

  public get visibleLocalAdditionalDocuments(): DocumentModel[] {
    return this.localAdditionalDocuments;
  }

  public additionalDocumentName(doc: DocumentModel): string {
    const documentTypeName = enumDocumentType[doc.type];
    const blTypeDocTitlePath = `ENTRY_SUMMARY.BILL_OF_LADING_DOCS.${documentTypeName}.TITLE`;
    const documentTypeTitlePath =
      doc.type === enumDocumentType.ISF_FORM ? 'LABELS.ISF_INSTRUCTION_SHEET' : `DOCUMENT_TYPE.${documentTypeName}`;

    const isBlDocTranslatedTextExist = !!this.utilsService.getTextAtPath(blTypeDocTitlePath);
    const translatedTitle = isBlDocTranslatedTextExist
      ? this.translateService.instant(blTypeDocTitlePath)
      : this.translateService.instant(documentTypeTitlePath);

    const entryDocsOfSameType = this.isfDocuments?.filter((d) => d.type === doc.type).length || 0;
    const localDocsOfSameType = this.visibleLocalAdditionalDocuments.filter((d) => d.type === doc.type);
    const localIndex = localDocsOfSameType.findIndex((d) => d.id === doc.id);
    const overallIndex = entryDocsOfSameType + localIndex;

    return overallIndex > 0 ? `${translatedTitle} #${overallIndex + 1}` : translatedTitle;
  }

  public onAddOtherDocument(type: enumDocumentType): void {
    const newDoc: DocumentModel = {
      id: this.additionalDocCounter--,
      type,
      url: null,
      relatedEntityType: enumDocumentRelatedEntityType.ISF,
      relatedEntityId: this.isf?.id,
      rootEntityType: enumDocumentRootEntityType.ISF,
      rootEntityId: this.isf?.id,
      fileName: null,
      createdDate: null,
      state: null,
    };
    const currentTypeCount = this.isfDocuments?.filter((d) => d.type === type && !!d.createdDate).length || 0;
    this.initialTypeCountsForLocalDocs.set(newDoc.id, currentTypeCount);
    this.localAdditionalDocuments = [...this.localAdditionalDocuments, newDoc];
  }

  public onUploadAdditionalDocument(file: File, doc: DocumentModel): void {
    this.uploadNewDocument.emit({ file, type: doc.type });
    this.uploadedLocalDocIds.add(doc.id);
    this.loadingLocalDocId = doc.id;
  }

  public onRemoveAdditionalSlot(doc: DocumentModel): void {
    this.uploadedLocalDocIds.delete(doc.id);
    this.initialTypeCountsForLocalDocs.delete(doc.id);
    this.localAdditionalDocuments = this.localAdditionalDocuments.filter((d) => d.id !== doc.id);
  }

  public onDownloadAllFiles(): void {
    this.downloadAll.emit();
  }
}
